'use client';

import React, { useState, useMemo } from 'react';
import {
  CITIES, RESOURCE_CATEGORIES, DATA_LAYERS, IRL_LENS_EXAMPLES,
  sortResourcesByProximity, Ward, ResourceItem
} from './cityData';
import IRLLensExpanded from './IRLLensExpanded';
import { BIOMES } from './ecoData';
import SimilarAyatSection from './SimilarAyatSection';

// SVG Icons per category
const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  Food: (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
      <circle cx="10" cy="8" r="5" />
      <path d="M10 3 Q12 1 14 3" />
      <path d="M7 13 Q10 18 13 13" />
    </svg>
  ),
  Health: (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
      <path d="M10 4 v12 M4 10 h12" />
      <rect x="3" y="3" width="14" height="14" rx="2" />
    </svg>
  ),
  Education: (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
      <path d="M3 8 L10 4 L17 8 L10 12 Z" />
      <path d="M6 10 v5 Q10 17 14 15 v-5" />
      <line x1="17" y1="8" x2="17" y2="14" />
    </svg>
  ),
  Income: (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
      <circle cx="10" cy="10" r="7" />
      <path d="M10 6 v1 M10 13 v1" />
      <path d="M8 8.5 Q8 7 10 7 Q12 7 12 9 Q12 11 10 11 Q12 11 12 13 Q12 15 10 15 Q8 15 8 13.5" />
    </svg>
  ),
  Housing: (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
      <path d="M3 10 L10 3 L17 10" />
      <path d="M5 10 v7 h10 v-7" />
      <rect x="8" y="13" width="4" height="4" />
    </svg>
  ),
  Reentry: (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
      <rect x="4" y="3" width="9" height="14" rx="1" />
      <path d="M13 8 L17 10 L13 12" />
      <line x1="9" y1="10" x2="17" y2="10" />
    </svg>
  ),
  Justice: (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
      <line x1="10" y1="2" x2="10" y2="18" />
      <line x1="6" y1="6" x2="14" y2="6" />
      <path d="M4 6 L2 12 h4 Z" />
      <path d="M16 6 L14 12 h4 Z" />
    </svg>
  ),
  Community: (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
      <circle cx="7" cy="7" r="3" />
      <circle cx="13" cy="7" r="3" />
      <path d="M2 17 Q2 13 7 13 Q10 13 10 15 Q10 13 13 13 Q18 13 18 17" />
    </svg>
  ),
};

// Arc Progress Bar Component
function ArcProgress({ value, max = 100, size = 80, strokeWidth = 6 }: { value: number; max?: number; size?: number; strokeWidth?: number }) {
  const radius = (size - strokeWidth * 2) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = Math.min(value / max, 1);
  // Use 270 degrees of arc (from 135deg to 405deg)
  const arcLength = circumference * 0.75;
  const dashOffset = arcLength * (1 - pct);
  const cx = size / 2;
  const cy = size / 2;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-[135deg]">
      {/* Track */}
      <circle
        cx={cx} cy={cy} r={radius}
        fill="none"
        stroke="rgba(255,255,255,0.1)"
        strokeWidth={strokeWidth}
        strokeDasharray={`${arcLength} ${circumference}`}
        strokeLinecap="round"
      />
      {/* Fill */}
      <circle
        cx={cx} cy={cy} r={radius}
        fill="none"
        stroke="white"
        strokeWidth={strokeWidth}
        strokeDasharray={`${arcLength} ${circumference}`}
        strokeDashoffset={dashOffset}
        strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.6s ease' }}
      />
    </svg>
  );
}

const PANEL_TABS = ['UBOOR', 'IRL LENS', 'RESOURCES', 'CONTEXT', 'DATA', 'EXTRACT'] as const;
type PanelTab = typeof PANEL_TABS[number];

export default function HaalResourceGuide() {
  const [activeCityId, setActiveCityId] = useState('chicago');
  const [selectedWardId, setSelectedWardId] = useState<string | null>(null);
  const [activeLayer, setActiveLayer] = useState('poverty');
  const [activeTab, setActiveTab] = useState<PanelTab>('UBOOR');
  const [viewMode, setViewMode] = useState<'map' | 'mosaic'>('mosaic');
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const [irlStep, setIrlStep] = useState(0);
  const [irlExample, setIrlExample] = useState(0);

  const city = useMemo(() => CITIES.find(c => c.id === activeCityId)!, [activeCityId]);
  const selectedWard = useMemo(() => city.wards.find(w => w.id === selectedWardId) ?? null, [city, selectedWardId]);

  const getLayerValue = (ward: Ward, layer: string): number =>
    ward.indicators[layer as keyof typeof ward.indicators] as number;

  const getLayerIntensity = (ward: Ward, layer: string): number => {
    const layerDef = DATA_LAYERS.find(l => l.key === layer);
    if (!layerDef) return 0;
    const val = getLayerValue(ward, layer);
    if (layer === 'medianIncome') return 1 - val / layerDef.max;
    return val / layerDef.max;
  };

  const getPrimaryIndicator = (ward: Ward) => {
    const layer = DATA_LAYERS.find(l => l.key === activeLayer);
    const val = getLayerValue(ward, activeLayer);
    if (activeLayer === 'medianIncome') return { label: layer?.label ?? '', display: `$${(val / 1000).toFixed(0)}K` };
    return { label: layer?.label ?? '', display: `${val}%` };
  };

  const sortedResources = useMemo(() => {
    if (!selectedWard) return [];
    return sortResourcesByProximity(selectedWard.resources, selectedWard.centerLat, selectedWard.centerLng);
  }, [selectedWard]);

  const resourcesByCategory = useMemo(() => {
    const byCategory: Record<string, ResourceItem[]> = {};
    sortedResources.forEach(r => {
      if (!byCategory[r.category]) byCategory[r.category] = [];
      byCategory[r.category].push(r);
    });
    return byCategory;
  }, [sortedResources]);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedText(label);
      setTimeout(() => setCopiedText(null), 2000);
    });
  };

  const severityBorder = (s: string) => s === 'CRITICAL' ? 'border-white' : s === 'HIGH' ? 'border-white/50' : 'border-white/25';
  const severityText = (s: string) => s === 'CRITICAL' ? 'text-white' : s === 'HIGH' ? 'text-white/70' : 'text-white/40';

  const currentIrlExample = IRL_LENS_EXAMPLES[irlExample];
  const currentIrlStep = currentIrlExample.steps[irlStep];

  return (
    <section className="py-16 bg-black border-t border-white/10" id="haal-resource-guide">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">

        {/* ── OPENING PREMISE: The whole earth is a Masjid ── */}
        <div className="mb-10 border border-white/10 p-6 bg-white/[0.02] relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          <div className="flex flex-col md:flex-row md:items-start gap-6">
            <div className="flex-1">
              <p className="font-mono text-[9px] tracking-[0.3em] text-white/20 uppercase mb-3">OPENING PREMISE — CITY RESOURCE SECTION</p>
              <p className="font-mono text-xl md:text-2xl font-black text-white uppercase tracking-tight mb-2 leading-tight">
                &ldquo;The Whole Earth is a Masjid&rdquo;
              </p>
              <p className="font-mono text-[10px] text-white/30 mb-4 leading-relaxed">
                جُعِلَتْ لِيَ الْأَرْضُ مَسْجِدًا وَطَهُورًا — Every ward, every city, every locality is a place of prostration. The caretaker&apos;s work is not separate from worship — it is worship. The city is not outside the Masjid — the city is the Masjid.
              </p>
              <p className="font-mono text-[9px] text-white/20 italic leading-relaxed max-w-2xl">
                This resource guide is built on that premise. Every disparity mapped here is a call to Islah. Every resource listed is a form of Amanah. Every caretaker using this tool is fulfilling a sacred trust — fisabilillah.
              </p>
            </div>
            {/* Islah = Salah etymology callout */}
            <div className="flex-shrink-0 border border-white/15 p-4 bg-white/[0.03] md:w-72">
              <p className="font-mono text-[8px] tracking-[0.25em] text-white/20 uppercase mb-3">ETYMOLOGY CALLOUT — HERO</p>
              <div className="mb-3">
                <p className="font-mono text-base font-black text-white mb-1">Islah = Salah</p>
                <p className="font-mono text-sm text-white/50">إِصْلَاح = صَلَاة</p>
              </div>
              <p className="font-mono text-[9px] text-white/40 leading-relaxed mb-3">
                The root of prayer is <span className="text-white/70">reform, restoration, and reconciliation</span>. ص-ل-ح (Ṣ-L-Ḥ) — the same root that gives us Salah (prayer) gives us Islah (reform). To pray is to reform. To reform is to pray.
              </p>
              <div className="flex flex-wrap gap-1">
                {['Salah', 'Islah', 'Sulh', 'Muslih'].map(word => (
                  <span key={word} className="font-mono text-[8px] text-white/40 border border-white/10 px-2 py-0.5">{word}</span>
                ))}
              </div>
              <p className="font-mono text-[8px] text-white/20 mt-2 italic">
                Root: ص-ل-ح · Righteousness · Repair · Peace
              </p>
            </div>
          </div>
        </div>

        {/* ECOSYSTEM HEADER */}
        <div className="mb-10 border-b border-white/10 pb-8">
          <p className="font-mono text-[10px] tracking-[0.3em] text-white/20 uppercase mb-2">THE HAAL PROJECT — ECOSYSTEM</p>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {['Keys to Kulli Haal', 'Ayat in Creation', 'Inner Reality Lens', 'Muslim Resource Guide'].map((step, i) => (
              <React.Fragment key={step}>
                <span className={`font-mono text-xs uppercase tracking-widest ${
                  step === 'Muslim Resource Guide' ? 'text-white font-black' : 'text-white/30'
                }`}>{step}</span>
                {i < 3 && <span className="text-white/20 font-mono text-xs">→</span>}
              </React.Fragment>
            ))}
          </div>
          <h2 className="font-mono text-2xl md:text-4xl font-black text-white uppercase tracking-tight mb-2">
            CARETAKER MATA — MUSLIM RESOURCE GUIDE
          </h2>
          <p className="font-mono text-xs text-white/30 max-w-3xl leading-relaxed">
            Haqq · Amanah · Islah — A living platform connecting caretakers to the resources, ayat, and Names of Allah that bridge disparity to provision. Each ward is a haal. Each disparity is a call to Islah. fisabilillah.
          </p>
        </div>

        {/* CITY TABS */}
        <div className="mb-6">
          <p className="font-mono text-[10px] tracking-widest text-white/20 uppercase mb-2">SELECT CITY — MADINA</p>
          <div className="flex flex-wrap gap-px border border-white/20">
            {CITIES.map(c => (
              <button
                key={c.id}
                onClick={() => { setActiveCityId(c.id); setSelectedWardId(null); setActiveTab('UBOOR'); }}
                className={`font-mono text-xs tracking-widest uppercase px-5 py-3 transition-colors flex-1 min-w-[120px] ${
                  activeCityId === c.id ? 'bg-white text-black' : 'text-white/40 hover:text-white/70 hover:bg-white/5'
                }`}
              >
                <span className="block">{c.name}</span>
                <span className="block text-[10px] opacity-60 mt-0.5">{c.arabicName}</span>
              </button>
            ))}
          </div>
          <p className="font-mono text-[10px] text-white/20 mt-2 leading-relaxed">{city.description}</p>
        </div>

        {/* AYAT LAYER BAR with icons */}
        <div className="mb-6">
          <p className="font-mono text-[10px] tracking-widest text-white/20 uppercase mb-2">AYAT LAYER — SELECT INDICATOR</p>
          <div className="flex flex-wrap gap-1">
            {DATA_LAYERS.map(layer => {
              const cat = RESOURCE_CATEGORIES.find(c => c.key.toLowerCase().includes(layer.key === 'poverty' ? 'income' : layer.key === 'foodDesert' ? 'food' : layer.key === 'gentrification' ? 'housing' : layer.key === 'noHSDiploma' ? 'education' : layer.key === 'uninsured' ? 'health' : layer.key === 'unemployment' ? 'income' : layer.key === 'vacancy' ? 'housing' : 'community'));
              const catKey = layer.key === 'poverty' || layer.key === 'unemployment' ? 'Income' :
                layer.key === 'foodDesert' ? 'Food' :
                layer.key === 'gentrification' || layer.key === 'vacancy' ? 'Housing' :
                layer.key === 'noHSDiploma' ? 'Education' :
                layer.key === 'uninsured' ? 'Health' : 'Community';
              return (
                <button
                  key={layer.key}
                  onClick={() => setActiveLayer(layer.key)}
                  className={`font-mono text-[10px] px-3 py-2 border transition-colors flex items-center gap-1.5 ${
                    activeLayer === layer.key ? 'bg-white text-black border-white' : 'text-white/30 border-white/15 hover:border-white/40 hover:text-white/60'
                  }`}
                >
                  <span className={activeLayer === layer.key ? 'text-black' : 'text-white/40'}>
                    {CATEGORY_ICONS[catKey]}
                  </span>
                  <span className="uppercase tracking-wider">{layer.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* VIEW TOGGLE */}
        <div className="flex gap-px border border-white/20 w-fit mb-6">
          <button onClick={() => setViewMode('mosaic')} className={`font-mono text-[10px] tracking-widest uppercase px-4 py-2 transition-colors ${ viewMode === 'mosaic' ? 'bg-white text-black' : 'text-white/40 hover:text-white/70' }`}>Tile Mosaic</button>
          <button onClick={() => setViewMode('map')} className={`font-mono text-[10px] tracking-widest uppercase px-4 py-2 border-l border-white/20 transition-colors ${ viewMode === 'map' ? 'bg-white text-black' : 'text-white/40 hover:text-white/70' }`}>Ward Map</button>
        </div>

        <div className="flex flex-col xl:flex-row gap-6">

          {/* WARD GRID */}
          <div className="flex-shrink-0">
            {viewMode === 'mosaic' ? (
              <div className="grid grid-cols-4 gap-px bg-white/10" style={{ width: 'min(480px, 100%)' }}>
                {city.wards.map(ward => {
                  const intensity = getLayerIntensity(ward, activeLayer);
                  const isSelected = selectedWardId === ward.id;
                  const primary = getPrimaryIndicator(ward);
                  return (
                    <button
                      key={ward.id}
                      onClick={() => { setSelectedWardId(ward.id); setActiveTab('UBOOR'); }}
                      className={`relative bg-black overflow-hidden transition-all ${ isSelected ? 'ring-2 ring-white ring-inset' : '' }`}
                      style={{ height: 110 }}
                    >
                      {/* SVG tile pattern */}
                      <svg width="100%" height="100%" viewBox="0 0 120 110" className="absolute inset-0">
                        {Array.from({ length: 7 }).map((_, i) => (
                          <line key={`h${i}`} x1="0" y1={i * 18} x2="120" y2={i * 18} stroke="white" strokeWidth="0.3" opacity="0.06" />
                        ))}
                        {Array.from({ length: 7 }).map((_, i) => (
                          <line key={`v${i}`} x1={i * 20} y1="0" x2={i * 20} y2="110" stroke="white" strokeWidth="0.3" opacity="0.06" />
                        ))}
                        <line x1="0" y1="0" x2="120" y2="110" stroke="white" strokeWidth="0.4" opacity="0.05" />
                        <line x1="120" y1="0" x2="0" y2="110" stroke="white" strokeWidth="0.4" opacity="0.05" />
                        {ward.severity === 'CRITICAL' && (
                          <>
                            <line x1="50" y1="40" x2="70" y2="60" stroke="white" strokeWidth="0.8" opacity="0.2" />
                            <line x1="70" y1="40" x2="50" y2="60" stroke="white" strokeWidth="0.8" opacity="0.2" />
                          </>
                        )}
                      </svg>

                      {/* Arc progress gauge */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="relative">
                          <ArcProgress value={intensity * 100} size={72} strokeWidth={5} />
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className={`font-mono font-black leading-none ${
                              isSelected ? 'text-white' : intensity > 0.7 ? 'text-white' : 'text-white/60'
                            }`} style={{ fontSize: '14px' }}>
                              {primary.display}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Ward label */}
                      <div className="absolute bottom-0 left-0 right-0 px-1 pb-1">
                        <p className={`font-mono font-black uppercase leading-tight text-center ${
                          isSelected ? 'text-white' : severityText(ward.severity)
                        }`} style={{ fontSize: '7px', letterSpacing: '0.05em' }}>
                          {ward.shortName}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              /* MAP VIEW */
              <div className="relative border border-white/10 bg-black" style={{ width: 'min(480px, 100%)', height: 380 }}>
                <svg width="100%" height="380" className="absolute inset-0 pointer-events-none">
                  <line x1="60" y1="190" x2="420" y2="190" stroke="white" strokeWidth="1" opacity="0.1" strokeDasharray="4,3" />
                  <line x1="240" y1="30" x2="240" y2="350" stroke="white" strokeWidth="1" opacity="0.1" strokeDasharray="4,3" />
                </svg>
                {city.wards.map(ward => {
                  const intensity = getLayerIntensity(ward, activeLayer);
                  const isSelected = selectedWardId === ward.id;
                  const primary = getPrimaryIndicator(ward);
                  return (
                    <button
                      key={ward.id}
                      onClick={() => { setSelectedWardId(ward.id); setActiveTab('UBOOR'); }}
                      className={`absolute border transition-all duration-200 ${
                        isSelected ? 'border-white z-10' : `${severityBorder(ward.severity)} hover:border-white/70`
                      }`}
                      style={{
                        left: ward.x, top: ward.y, width: ward.w, height: ward.h,
                        backgroundColor: `rgba(255,255,255,${isSelected ? 0.15 : intensity * 0.22})`,
                      }}
                    >
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-1 gap-0.5">
                        <span className={`font-mono font-black uppercase leading-tight text-center ${
                          isSelected ? 'text-white' : severityText(ward.severity)
                        }`} style={{ fontSize: '8px' }}>{ward.shortName}</span>
                        <span className={`font-mono font-black ${
                          isSelected ? 'text-white' : 'text-white/70'
                        }`} style={{ fontSize: '13px' }}>{primary.display}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Data matrix */}
            <div className="mt-4 border border-white/10 overflow-x-auto" style={{ maxWidth: 'min(480px, 100%)' }}>
              <table className="w-full min-w-[400px]">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="font-mono text-[9px] text-white/20 uppercase text-left px-2 py-1.5">Ward</th>
                    <th className="font-mono text-[9px] text-white/20 uppercase text-left px-2 py-1.5">Sev.</th>
                    <th className="font-mono text-[9px] text-white/20 uppercase text-right px-2 py-1.5">Poverty</th>
                    <th className="font-mono text-[9px] text-white/20 uppercase text-right px-2 py-1.5">Food</th>
                    <th className="font-mono text-[9px] text-white/20 uppercase text-right px-2 py-1.5">Unemp.</th>
                  </tr>
                </thead>
                <tbody>
                  {city.wards.map(ward => (
                    <tr
                      key={ward.id}
                      onClick={() => { setSelectedWardId(ward.id); setActiveTab('UBOOR'); }}
                      className={`border-b border-white/5 cursor-pointer transition-colors hover:bg-white/5 ${ selectedWardId === ward.id ? 'bg-white/10' : '' }`}
                    >
                      <td className="font-mono text-[9px] text-white px-2 py-1.5">{ward.shortName}</td>
                      <td className={`font-mono text-[9px] px-2 py-1.5 ${severityText(ward.severity)}`}>{ward.severity.slice(0,4)}</td>
                      <td className="font-mono text-[9px] text-white/50 text-right px-2 py-1.5">{ward.indicators.poverty}%</td>
                      <td className="font-mono text-[9px] text-white/50 text-right px-2 py-1.5">{ward.indicators.foodDesert}%</td>
                      <td className="font-mono text-[9px] text-white/50 text-right px-2 py-1.5">{ward.indicators.unemployment}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* DETAIL PANEL */}
          <div className="flex-1 min-w-0">
            {selectedWard ? (
              <div className="border border-white/20">
                {/* Ward header */}
                <div className="p-4 border-b border-white/10">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-mono text-[10px] text-white/30 uppercase tracking-widest mb-1">{selectedWard.severity} — {city.name.toUpperCase()}</p>
                      <h3 className="font-mono text-xl font-black text-white uppercase leading-tight">{selectedWard.name}</h3>
                      {/* Biome display */}
                      {(() => {
                        const cityName = city.name.charAt(0).toUpperCase() + city.name.slice(1);
                        const biome = BIOMES[cityName];
                        return biome ? (
                          <div className="flex items-center gap-1.5 mt-1">
                            <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#2d7a47' }} />
                            <span className="font-mono text-[9px]" style={{ color: '#2d7a47' }}>{biome.name}</span>
                            <span className="font-serif text-xs" style={{ color: 'rgba(45,122,71,0.5)' }}>{biome.ar}</span>
                          </div>
                        ) : null;
                      })()}
                      <div className="flex flex-wrap gap-3 mt-2">
                        {selectedWard.disparities.slice(0, 3).map((d, i) => (
                          <div key={i} className="flex items-center gap-1.5">
                            <span className="font-mono text-white font-black" style={{ fontSize: '18px' }}>{d.value}</span>
                            <span className="font-mono text-[9px] text-white/30 uppercase">{d.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <button onClick={() => setSelectedWardId(null)} className="font-mono text-xs text-white/30 hover:text-white transition-colors flex-shrink-0">✕</button>
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex overflow-x-auto border-b border-white/10">
                  {PANEL_TABS.map(tab => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`font-mono text-[9px] tracking-widest uppercase px-3 py-2.5 whitespace-nowrap border-r border-white/10 last:border-r-0 transition-colors flex-shrink-0 ${
                        activeTab === tab ? 'bg-white text-black' : 'text-white/30 hover:text-white/60 hover:bg-white/5'
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>

                {/* Tab content */}
                <div className="p-4 max-h-[520px] overflow-y-auto">

                  {/* UBOOR TAB */}
                  {activeTab === 'UBOOR' && (
                    <div>
                      <div className="mb-4 p-3 border border-white/10 bg-white/5">
                        <p className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-1">Open Letter to Incarcerated Caretakers</p>
                        <p className="font-mono text-xs text-white/50 italic leading-relaxed">&ldquo;{selectedWard.openLetterQuote}&rdquo;</p>
                      </div>
                      <p className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-3">Disparities as Islah Opportunities</p>
                      <div className="space-y-3">
                        {selectedWard.disparities.map((d, i) => (
                          <div key={i} className="border border-white/10 p-3">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div>
                                <p className="font-mono text-xs font-black text-white uppercase">{d.name}</p>
                                <p className="font-mono text-[9px] text-white/80">{d.value}</p>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <p className="font-mono text-sm text-white/80">{d.arabicName}</p>
                                <p className="font-mono text-[9px] text-white/50">{d.nameOfAllah}</p>
                                <p className="font-mono text-[9px] text-white/30 italic">{d.meaning}</p>
                              </div>
                            </div>
                            <p className="font-mono text-xs text-white/50 leading-relaxed">{d.islahCall}</p>
                          </div>
                        ))}
                      </div>
                      {/* Pivotal Moments */}
                      {selectedWard.pivotalMoments && selectedWard.pivotalMoments.length > 0 && (
                        <div className="mt-4">
                          <p className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-2">Pivotal Moments — Huduth Zamani</p>
                          <div className="space-y-1">
                            {selectedWard.pivotalMoments.map((m, i) => (
                              <div key={i} className="flex gap-3 border-l border-white/10 pl-3">
                                <span className="font-mono text-[9px] text-white/40 flex-shrink-0 w-16">{m.year}</span>
                                <span className="font-mono text-[9px] text-white/50 leading-relaxed">{m.event}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* IRL LENS TAB */}
                  {activeTab === 'IRL LENS' && (
                    <div>
                      <div className="mb-4 p-3 border border-white/10 bg-white/5">
                        <p className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-1">Inner Reality Lens — Caretaker’s Journey</p>
                        <p className="font-mono text-xs text-white/40 italic leading-relaxed">&ldquo;The Inner Reality Lens unfolds Haqq through gathered sensory information, linking Names/Attributes to Amanah — bridging the Ayat in Creation to the Muslim Resource Guide.&rdquo;</p>
                      </div>

                      {/* Example selector */}
                      <div className="flex gap-1 mb-4">
                        {IRL_LENS_EXAMPLES.map((ex, i) => (
                          <button
                            key={ex.id}
                            onClick={() => { setIrlExample(i); setIrlStep(0); }}
                            className={`font-mono text-[9px] uppercase tracking-wider px-3 py-2 border transition-colors flex items-center gap-1.5 ${
                              irlExample === i ? 'bg-white text-black border-white' : 'text-white/30 border-white/15 hover:text-white/60'
                            }`}
                          >
                            <span>{ex.icon}</span>
                            <span>{ex.title}</span>
                          </button>
                        ))}
                      </div>

                      {/* Step progress bar */}
                      <div className="flex gap-1 mb-4">
                        {currentIrlExample.steps.map((step, i) => (
                          <button
                            key={i}
                            onClick={() => setIrlStep(i)}
                            className={`flex-1 h-1.5 transition-colors ${
                              i <= irlStep ? 'bg-white' : 'bg-white/15'
                            }`}
                          />
                        ))}
                      </div>

                      {/* Current step */}
                      <div className="border border-white/20 p-4">
                        <div className="flex items-start justify-between gap-2 mb-3">
                          <div>
                            <p className="font-mono text-[9px] text-white/30 uppercase tracking-widest">{currentIrlStep.stage}</p>
                            <p className="font-mono text-lg font-black text-white">{currentIrlStep.arabic}</p>
                            <p className="font-mono text-[9px] text-white/50 uppercase tracking-wider">{currentIrlStep.title}</p>
                          </div>
                          <span className="text-2xl">{currentIrlExample.icon}</span>
                        </div>
                        <p className="font-mono text-xs text-white/60 leading-relaxed mb-3">{currentIrlStep.content}</p>

                        {/* Names of Allah for Sunnat Allah step */}
                        {'namesOfAllah' in currentIrlStep && currentIrlStep.namesOfAllah && (
                          <div className="space-y-2 mb-3">
                            {(currentIrlStep.namesOfAllah as Array<{arabic: string; transliteration: string; meaning: string; process: string}>).map((n, i) => (
                              <div key={i} className="flex items-start gap-3 border border-white/10 p-2">
                                <span className="font-mono text-base text-white/70 flex-shrink-0">{n.arabic}</span>
                                <div>
                                  <p className="font-mono text-[9px] text-white/60">{n.transliteration} — {n.meaning}</p>
                                  <p className="font-mono text-[9px] text-white/30 italic">{n.process}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Ayat */}
                        {'ayat' in currentIrlStep && currentIrlStep.ayat && (
                          <div className="border border-white/10 p-3 bg-white/5">
                            <p className="font-mono text-sm text-white/70 text-right mb-1">{(currentIrlStep.ayat as {arabic: string; ref: string; text: string}).arabic}</p>
                            <p className="font-mono text-[9px] text-white/40 italic leading-relaxed">&ldquo;{(currentIrlStep.ayat as {arabic: string; ref: string; text: string}).text}&rdquo;</p>
                            <p className="font-mono text-[9px] text-white/20 mt-1">Quran {(currentIrlStep.ayat as {arabic: string; ref: string; text: string}).ref}</p>
                          </div>
                        )}

                        {/* UBOOR bridge — connect to ward resources */}
                        {currentIrlStep.stage === 'UBOOR' && (
                          <div className="mt-3 border border-white/20 p-3 bg-white/5">
                            <p className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-2">Bridge to {selectedWard.name} Resources</p>
                            {selectedWard.resources
                              .filter(r => r.category === currentIrlExample.resourceCategory)
                              .slice(0, 2)
                              .map((r, i) => (
                                <div key={i} className="mb-2">
                                  <p className="font-mono text-[9px] font-black text-white uppercase">{r.name}</p>
                                  <p className="font-mono text-[9px] text-white/40">{r.arabicName} — {r.nameOfAllah}; {r.meaning}</p>
                                  <p className="font-mono text-[9px] text-white/30">{r.address}</p>
                                </div>
                              ))
                            }
                          </div>
                        )}
                      </div>

                      {/* Navigation */}
                      <div className="flex justify-between mt-3">
                        <button
                          onClick={() => setIrlStep(Math.max(0, irlStep - 1))}
                          disabled={irlStep === 0}
                          className="font-mono text-[9px] uppercase tracking-wider px-4 py-2 border border-white/20 text-white/40 hover:text-white hover:border-white disabled:opacity-20 transition-colors"
                        >
                          ← Prev
                        </button>
                        <span className="font-mono text-[9px] text-white/20">{irlStep + 1} / {currentIrlExample.steps.length}</span>
                        <button
                          onClick={() => setIrlStep(Math.min(currentIrlExample.steps.length - 1, irlStep + 1))}
                          disabled={irlStep === currentIrlExample.steps.length - 1}
                          className="font-mono text-[9px] uppercase tracking-wider px-4 py-2 border border-white/20 text-white/40 hover:text-white hover:border-white disabled:opacity-20 transition-colors"
                        >
                          Next →
                        </button>
                      </div>

                      {/* ── EXPANDED IRL LENS ── */}
                      <div className="mt-6 border-t border-white/10 pt-5">
                        <p className="font-mono text-[9px] text-white/20 uppercase tracking-widest mb-1">EXPANDED LENS — WARD-SPECIFIC ENCOUNTERS · SEASONAL · AYAT BRIDGE · DEEP DIVE</p>
                        <p className="font-mono text-[9px] text-white/30 leading-relaxed mb-4">
                          Ward-specific IRL encounters, seasonal lens (auto-detected), Ayat Bridge (8 Names as vectors), Deep Dive layers (Fission Reactor · Hawas-to-Hikmah · STEM as Sunnat Allah), Caretaker submissions, and the living catalog.
                        </p>
                        <IRLLensExpanded
                          wardId={selectedWard.id}
                          wardName={selectedWard.name}
                          cityId={activeCityId}
                          foodDesertPct={selectedWard.indicators.foodDesert}
                          resourceCategories={RESOURCE_CATEGORIES.map(c => c.key)}
                        />
                      </div>

                      {/* SIMILAR AYAT ACROSS CITIES */}
                      <SimilarAyatSection
                        currentCity={activeCityId}
                      />
                    </div>
                  )}

                  {/* RESOURCES TAB */}
                  {activeTab === 'RESOURCES' && (
                    <div>
                      <p className="font-mono text-[9px] text-white/20 uppercase tracking-widest mb-3">
                        Resources ordered by proximity to {selectedWard.shortName} geographic center
                      </p>
                      {Object.entries(resourcesByCategory).map(([cat, resources]) => {
                        const catDef = RESOURCE_CATEGORIES.find(c => c.key === cat);
                        return (
                          <div key={cat} className="mb-6">
                            {/* Category header with icon, Arabic, Ayat */}
                            <div className="border border-white/20 p-3 mb-3 bg-white/5">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="text-white/60">{CATEGORY_ICONS[cat] ?? CATEGORY_ICONS.Community}</span>
                                {catDef && (
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="font-mono text-base text-white/80">{catDef.arabic}</span>
                                      <span className="font-mono text-xs font-black text-white uppercase">{catDef.nameOfAllah}</span>
                                      <span className="font-mono text-[9px] text-white/40">{catDef.meaning}</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                              {catDef && (
                                <>
                                  <p className="font-mono text-xs text-white/60 text-right mb-1">{catDef.ayat.arabic}</p>
                                  <p className="font-mono text-[9px] text-white/40 italic leading-relaxed">&ldquo;{catDef.ayat.text}&rdquo; — Quran {catDef.ayat.ref}</p>
                                  <p className="font-mono text-[9px] text-white/30 mt-2 leading-relaxed">{catDef.context}</p>
                                </>
                              )}
                            </div>
                            <div className="space-y-2">
                              {resources.map((r, i) => (
                                <div key={i} className="border border-white/10 p-3">
                                  <div className="flex items-start justify-between gap-2 mb-1">
                                    <p className="font-mono text-xs font-black text-white uppercase">{r.name}</p>
                                    <span className="font-mono text-[9px] text-white/30 flex-shrink-0">#{i + 1}</span>
                                  </div>
                                  <p className="font-mono text-[9px] text-white/60 mb-1">
                                    {r.arabicName} — {r.nameOfAllah}; {r.meaning}
                                  </p>
                                  <p className="font-mono text-[9px] text-white/30 mb-1">{r.address}</p>
                                  <p className="font-mono text-[9px] text-white/30 mb-2">{r.contact}</p>
                                  <p className="font-mono text-[9px] text-white/50 leading-relaxed">{r.desc}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* CONTEXT TAB */}
                  {activeTab === 'CONTEXT' && (
                    <div>
                      <p className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-3">Historical Context — Huduth Zamani</p>
                      <p className="font-mono text-xs text-white/60 leading-relaxed mb-4">{selectedWard.history}</p>
                      <div className="mb-4">
                        <p className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-2">Community Anchors</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedWard.anchors.map(a => (
                            <span key={a} className="font-mono text-[9px] text-white/40 border border-white/15 px-2 py-1">{a}</span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-2">Masajid</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedWard.masajid.map(m => (
                            <span key={m} className="font-mono text-[9px] text-white border border-white/30 px-2 py-1">{m}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* DATA TAB */}
                  {activeTab === 'DATA' && (
                    <div>
                      <p className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-3">Socioeconomic Indicators</p>
                      <div className="space-y-3">
                        {DATA_LAYERS.map(layer => {
                          const val = getLayerValue(selectedWard, layer.key);
                          const intensity = getLayerIntensity(selectedWard, layer.key);
                          const displayVal = layer.key === 'medianIncome' ? `$${val.toLocaleString()}` : `${val}%`;
                          return (
                            <div key={layer.key}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-mono text-[9px] text-white/40 uppercase">{layer.label}</span>
                                <span className="font-mono font-black text-white" style={{ fontSize: '16px' }}>{displayVal}</span>
                              </div>
                              <div className="h-1.5 bg-white/10">
                                <div className="h-full bg-white transition-all" style={{ width: `${intensity * 100}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="mt-4 border-t border-white/10 pt-4">
                        <p className="font-mono text-[9px] text-white/20 uppercase tracking-widest mb-2">Sources</p>
                        <p className="font-mono text-[9px] text-white/20 leading-relaxed">US Census ACS 5-Year Estimates · USDA Food Access Research Atlas · Urban Displacement Project · ARC/CMAP/MORPC Open Data · Neighborhood Nexus</p>
                      </div>
                    </div>
                  )}

                  {/* EXTRACT TAB */}
                  {activeTab === 'EXTRACT' && (
                    <div className="space-y-3">
                      <p className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-3">Overpass API Export — Tile Extraction</p>
                      <p className="font-mono text-[9px] text-white/40 leading-relaxed mb-3">{selectedWard.fieldNotes}</p>
                      {[
                        { label: 'BBOX Coordinates', value: `${selectedWard.bbox.south},${selectedWard.bbox.west},${selectedWard.bbox.north},${selectedWard.bbox.east}` },
                        { label: 'Overpass QL Query', value: `[out:xml][timeout:25];\n(\n  way["highway"](${selectedWard.bbox.south},${selectedWard.bbox.west},${selectedWard.bbox.north},${selectedWard.bbox.east});\n  way["waterway"](${selectedWard.bbox.south},${selectedWard.bbox.west},${selectedWard.bbox.north},${selectedWard.bbox.east});\n  way["railway"](${selectedWard.bbox.south},${selectedWard.bbox.west},${selectedWard.bbox.north},${selectedWard.bbox.east});\n);\nout body;\n>;\nout skel qt;` },
                        { label: 'Overpass Turbo URL', value: `https://overpass-turbo.eu/?bbox=${selectedWard.bbox.west},${selectedWard.bbox.south},${selectedWard.bbox.east},${selectedWard.bbox.north}` },
                      ].map(item => (
                        <div key={item.label} className="border border-white/10 p-3">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-mono text-[9px] text-white/30 uppercase tracking-widest">{item.label}</p>
                            <button
                              onClick={() => copyToClipboard(item.value, item.label)}
                              className="font-mono text-[9px] text-white/40 hover:text-white border border-white/15 px-2 py-0.5 transition-colors"
                            >
                              {copiedText === item.label ? 'COPIED' : 'COPY'}
                            </button>
                          </div>
                          <p className="font-mono text-[9px] text-white/40 break-all leading-relaxed whitespace-pre-wrap">{item.value}</p>
                        </div>
                      ))}
                    </div>
                  )}

                </div>
              </div>
            ) : (
              /* Empty state */
              <div className="border border-white/10 p-8 text-center flex flex-col items-center justify-center min-h-96">
                <div className="mb-6">
                  <p className="font-mono text-[9px] text-white/20 uppercase tracking-widest mb-4">THE HAAL PROJECT ECOSYSTEM</p>
                  <div className="space-y-2">
                    {['Keys to Kulli Haal', 'Ayat in Creation', 'Inner Reality Lens', 'Muslim Resource Guide'].map((branch, i) => (
                      <div key={branch} className="flex items-center gap-3 justify-center">
                        <span className="font-mono text-[9px] text-white/20 w-4">{i + 1}</span>
                        <span className={`font-mono text-xs uppercase tracking-wider ${
                          i === 3 ? 'text-white font-black' : 'text-white/30'
                        }`}>{branch}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <p className="font-mono text-[9px] text-white/20 uppercase tracking-widest mb-2">Select a Ward</p>
                <p className="font-mono text-xs text-white/30 leading-relaxed max-w-xs">
                  Click any ward tile to explore its disparities, resources, historical context, and IRL Lens walkthroughs.
                </p>
                <p className="font-mono text-[9px] text-white/15 mt-6 italic">
                  &ldquo;We were commanded to come down for a time as Caretakers — to see what is broken and to bridge it.&rdquo;
                </p>
                <p className="font-mono text-[9px] text-white/10 mt-2">fisabilillah</p>
              </div>
            )}
          </div>
        </div>

        {/* ── CARETAKER ROLE-MATCHING ── */}
        <div className="mt-10 border border-white/10 p-6 bg-white/[0.02]">
          <p className="font-mono text-[9px] tracking-[0.25em] text-white/20 uppercase mb-4">CARETAKER ROLE-MATCHING — CITY RESOURCES</p>
          <p className="font-mono text-xs text-white/40 leading-relaxed mb-6 max-w-2xl">
            Based on your caretaker role and locality, here are the most relevant city resources. Select your role to see suggested resources and entry points into the guide.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              {
                role: 'Community Caretaker',
                ar: 'رَاعِي الْمُجْتَمَع',
                icon: '⬡',
                color: '#2d7a47',
                resources: ['Community Connect', 'Masajid Network', 'Food Access', 'Housing Support'],
                entry: 'Start with the Community tab — find masajid, food banks, and community anchors in your ward.',
                cities: ['Chicago', 'Atlanta', 'Detroit'],
              },
              {
                role: 'Justice Advocate',
                ar: 'مُنَاضِل الْعَدَالَة',
                icon: '⚖️',
                color: '#c9a84c',
                resources: ['Reentry Support', 'Legal Aid', 'Justice-Involved', 'Advocacy Networks'],
                entry: 'Start with the Justice-Involved section — reentry resources, legal aid, and advocacy networks.',
                cities: ['Chicago', 'Atlanta', 'Portland', 'Detroit'],
              },
              {
                role: 'Inner Seeker',
                ar: 'طَالِب الْبَاطِن',
                icon: '◎',
                color: '#8a8ab8',
                resources: ['IRL Lens', 'Ayat Layer', 'Names Reference', 'Spiritual Resources'],
                entry: 'Start with the IRL Lens tab — connect ward disparities to Names of Allah and Ayat in creation.',
                cities: ['All Cities'],
              },
              {
                role: 'Academic Researcher',
                ar: 'بَاحِث أَكَادِيمِي',
                icon: '✦',
                color: '#d4a82a',
                resources: ['Data Layer', 'Historical Context', 'UBOOR Analysis', 'Extract Tools'],
                entry: 'Start with the Data and Context tabs — socioeconomic indicators, historical context, and Overpass API extraction.',
                cities: ['All Cities'],
              },
              {
                role: 'Local Resource Provider',
                ar: 'مُزَوِّد الْمَوَارِد',
                icon: '◈',
                color: '#2d8a87',
                resources: ['Resource Listing', 'Community Connect', 'Ward Mapping', 'Network Building'],
                entry: 'Start with the Resources tab — see how resources are listed and connect your services to the network.',
                cities: ['Chicago', 'Atlanta', 'Portland', 'Detroit'],
              },
              {
                role: 'Returning Citizen',
                ar: 'مُعَائِد',
                icon: '→',
                color: '#5a5a8a',
                resources: ['Reentry Support', 'Housing', 'Employment', 'Community Anchors'],
                entry: 'Start with Reentry resources — housing, employment, legal aid, and community anchors in your city.',
                cities: ['Chicago', 'Detroit', 'Atlanta'],
              },
            ].map(item => (
              <div key={item.role} className="border border-white/10 p-4 hover:border-white/20 transition-colors">
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-lg flex-shrink-0 mt-0.5">{item.icon}</span>
                  <div>
                    <p className="font-mono text-xs font-black text-white uppercase">{item.role}</p>
                    <p className="font-mono text-[9px]" style={{ color: item.color }}>{item.ar}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mb-3">
                  {item.resources.map(r => (
                    <span key={r} className="font-mono text-[8px] text-white/30 border border-white/10 px-1.5 py-0.5">{r}</span>
                  ))}
                </div>
                <p className="font-mono text-[9px] text-white/40 leading-relaxed mb-2">{item.entry}</p>
                <div className="flex flex-wrap gap-1">
                  {item.cities.map(city => (
                    <span key={city} className="font-mono text-[8px] px-1.5 py-0.5" style={{ color: item.color, border: `1px solid ${item.color}25`, background: `${item.color}08` }}>{city}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer tagline */}
        <div className="mt-12 border-t border-white/10 pt-6 text-center">
          <p className="font-mono text-[9px] text-white/20 uppercase tracking-widest">
            THE HAAL PROJECT — CARETAKER MATA — HAQQ · AMANAH · ISLAH
          </p>
          <p className="font-mono text-[9px] text-white/10 mt-1">
            A garden of caretakers, no walls, an open toolkit — for us, by us, fisabilillah
          </p>
        </div>

      </div>
    </section>
  );
}
